import { Slider, SliderChangeEvent, SliderLabel, Switch } from "@progress/kendo-react-inputs";
import { Card, CardActions, CardBody, CardHeader, CardSubtitle, CardTitle, StackLayout } from "@progress/kendo-react-layout";
import { useCallback, useContext, useEffect, useState } from "react";
import { useLocalization } from "@progress/kendo-react-intl";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { locales } from "../resources/locales";
import { AppContext } from "../AppContext";
import { ThemeChooser } from "../components/ThemeChooser";
import { Button } from "@progress/kendo-react-buttons";
import { AlarmsTile } from "../components/dashboard/AlarmsTile";
import { Devices } from "../components/dashboard/Devices";
import { DevicesBatteryStatus } from "../components/dashboard/DevicesBatteryStatus";
import { DevicesTileCounter } from "../components/dashboard/DevicesTileCounter";
import { RegularTag } from "../components/dashboard/RegularTag";
import packageJson from '../../package.json';
import axios from "axios";
import { AccessToken } from "../config/AccessToken";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

/**
 * @description
 * This component is used to display the settings of the app.
 * The user can toggle the visibility of the widgets and change the refresh time of the API.
 * The user can also change the regular tag and the battery percentage for the unloaded tags.
 * The user can also reset the layout of the widgets to its initial state.
 * The user can also change the language of the app.
 * @param {any} props The props passed to the component.
 * @returns {JSX.Element} The component to be rendered.
 * @example
 * import { Settings } from '../components/Settings';
 * 
 * <Settings />
 */
export const Settings = (props: any) => {

    const localizationService = useLocalization();

    const { localeId, onLanguageChange } = useContext(AppContext);
    const currentLanguage = locales.find(item => item.localeId === localeId);
    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const [instance, accessTokenRequest] = AccessToken();

    const { changeTheme, theme } = props;

    const [isHorizontal, setIsHorizontal] = useState(true);

    const horizontalOnClick = useCallback(() => {
        setIsHorizontal(true);
    }, [setIsHorizontal]);

    const verticalOnClick = useCallback(() => {
        setIsHorizontal(false);
    }, [setIsHorizontal]);

    const userEmail = localStorage.getItem('email');
    const languageId = localStorage.getItem('languageId');
    const accountId = localStorage.getItem('accountId');

    const defaultValueRegularTag = [0, 6, 12, 18, 24, 30, 36, 42, 48];
    const [valueRegularTag, setValueRegularTag] = useState(5);

    const [ clientConfig, setClientConfig ] = useState<any>([]); 


    let initialTiles: any = [
        {
            id: 0,
            defaultPosition: { col: 1, colSpan: 2, rowSpan: 1, order: 1 },
            header: localizationService.toLanguageString('dashboard.widget.devices.title', 'NOT_FOUND'),
            body:
                <Devices
                //rowSpan={data[0]?.['rowSpan']}
                //colSpan={data[0]?.['colSpan']}
                />,
            //resizable: false,
            visible: true
        },
        {
            id: 1,
            defaultPosition: { col: 3, colSpan: 1, rowSpan: 1, order: 2 },
            header: localizationService.toLanguageString('dashboard.widget.devices_counter', 'NOT_FOUND'),
            body:
                <DevicesTileCounter />,
            //resizable: false,
            visible: true
        },
        {
            id: 2,
            defaultPosition: { col: 4, colSpan: 1, rowSpan: 1, order: 3 },
            header: localizationService.toLanguageString('dashboard.widget.alarms_counter', 'NOT_FOUND'),
            body:
                <AlarmsTile />,
            //resizable: false,
            visible: true
        },
        {
            id: 3,
            defaultPosition: { col: 5, colSpan: 2, rowSpan: 1, order: 4 },
            header: localizationService.toLanguageString('dashboard.widget.regular_tag_2', 'NOT_FOUND'),
            body:
                <RegularTag />,
            //resizable: false,
            visible: true
        },
        {
            id: 4,
            defaultPosition: { col: 7, colSpan: 2, rowSpan: 2, order: 5 },
            header: localizationService.toLanguageString('dashboard.widget.devices.battery_status_2', 'NOT FOUND'),
            body:
                <DevicesBatteryStatus
                    rowSpan={2}
                    colSpan={2}
                />,
            visible: true
            // resizable: (rowSpan && colSpan) == 1 || (rowSpan && colSpan) == 2 ? true : false,  
        },
    ];

    const stringify = (obj: any) => {
        let cache: any = [];
        let str = JSON.stringify(obj, function (key, value) {
            if (typeof value === "object" && value !== null) {
                if (cache.indexOf(value) !== -1) {
                    // Circular reference found, discard key
                    return;
                }
                // Store value in our collection
                cache.push(value);
            }
            return value;
        });
        cache = null; // reset the cache
        return str;
    }

    const removeCharsUntilClosingParenthesis = (str: any) => {
        const openParenIndex = str.indexOf('(');
        if (openParenIndex !== -1) {
            return str.substring(0, openParenIndex);
        } else {
            return str;
        }
    }

    const [tiles, setTiles] = useState(() => {
        const savedState = localStorage.getItem(`tileState: ${userEmail}`);
        const parsedState = savedState ? JSON.parse(savedState) : [];

        return initialTiles.map((initialTile: any) => {
            const savedTile = parsedState.find((tile: any) => tile.id === initialTile.id);
            //if(initialTile.header.includes('Tag regolari') || initialTile.header.includes('Regular tag')) {
            //    removeCharsStartingWith(initialTile.header, '(')  
            //}
            return savedTile ? { ...initialTile, ...savedTile } : initialTile;
        });
    });

    const onResetLayoutWidget = () => {
        localStorage.removeItem(`tileState: ${userEmail}`);
        setTiles(initialTiles); // Reset to initial tiles
    };

    const onLanguageChangeSystem: any = useCallback((event: any) => {
        localStorage.setItem('languageId', event.value.localeId)
    }, [languageId])

    useEffect(() => {
        saveTilesToLocalStorage(tiles);
    }, [tiles]);

    //useEffect(() => {
    //    updateTilesToLocalStorage(tiles);
    //}, [onLanguageChangeSystem]);

    const saveTilesToLocalStorage = (tiles: any[]) => {
        const tileStateToSave = tiles.map(({ id, header, defaultPosition, visible }) => ({
            id,
            header,
            defaultPosition,
            visible,
        }));
        localStorage.setItem(`tileState: ${userEmail}`, JSON.stringify(tileStateToSave));
    };

    const updateTilesToLocalStorage = (tiles: any[]) => {
        tiles.map((initialTile: any) => {
            if (languageId === 'en-US') {
                initialTile.header = initialTile.header == 'Tag' ? 'Tag'
                    : initialTile.header == 'Total tag' ? 'Tag totali'
                        : initialTile.header == 'Total alarms' ? 'Allarmi totali'
                            : initialTile.header == 'Tags (battery status)' ? 'Tags (stato batteria)'
                                //: initialTile.header == `Maximum update delay for regular devices(${localStorage.getItem('valueRegularTag')}H)` ? `Ritardo massimo di aggiornamento per dispositivi regolari(${localStorage.getItem('valueRegularTag')}H)` 
                                : initialTile.header == `Devices online(${localStorage.getItem('valueRegularTag')}H)` ? `Dispositivi online(${localStorage.getItem('valueRegularTag')}H)`
                                    : ''
            } else {
                initialTile.header = initialTile.header == 'Tag' ? 'Tag' : initialTile.header == 'Tag totali' ? 'Total tag'
                    : initialTile.header == 'Allarmi totali' ? 'Total alarms'
                        : initialTile.header == 'Tags (stato batteria)' ? 'Tags (battery status)'
                            //: initialTile.header == `Ritardo massimo di aggiornamento per dispositivi regolari(${localStorage.getItem('valueRegularTag')}H)` ? `Maximum update delay for regular devices(${localStorage.getItem('valueRegularTag')}H)`
                            : initialTile.header == `Dispositivi online(${localStorage.getItem('valueRegularTag')}H)` ? `Devices online(${localStorage.getItem('valueRegularTag')}H)`
                                : ''
            }
        })

        const getHeader = (header: string, languageId: any) => {
            if (languageId === 'it-IT') {
                switch (header) {
                    case 'Tag':
                        return 'Tag';
                    case 'Tag totali':
                        return 'Total tag';
                    case 'Allarmi totali':
                        return 'Total alarms';
                    case 'Tags (stato batteria)':
                        return 'Tags (battery status)';
                    case `Dispositivi online(${localStorage.getItem('valueRegularTag')}H)`:
                        return `Devices online(${localStorage.getItem('valueRegularTag')}H)`;
                    //case `Ritardo massimo di aggiornamento per dispositivi regolari(${localStorage.getItem('valueRegularTag')}H)`:
                    //    return `Maximum update delay for regular devices(${localStorage.getItem('valueRegularTag')}H)`;
                    default:
                        return header;
                }
            } else {
                switch (header) {
                    case 'Tag':
                        return 'Tag';
                    case 'Total tag':
                        return 'Tag totali';
                    case 'Total alarms':
                        return 'Allarmi totali';
                    case 'Tags (battery status)':
                        return 'Tags (stato batteria)';
                    case `Regular tag(${localStorage.getItem('valueRegularTag')}H)`:
                        return `Tag regolari(${localStorage.getItem('valueRegularTag')}H)`;
                    //case `Maximum update delay for regular devices(${localStorage.getItem('valueRegularTag')}H)`:
                    //    return `Ritardo massimo di aggiornamento per dispositivi regolari(${localStorage.getItem('valueRegularTag')}H)`;
                    default:
                        return header;
                }
            }
        };


        const tileStateToSave = tiles.map(({ id, header, defaultPosition, visible }) => (
            {
                id,
                header: getHeader(header, languageId),
                //header: (
                //    header == 'Tag' ? 'Tag' 
                //    : header == 'Tag totali' ? 'Total tag' : header == 'Total tag' ? 'Tag totali' 
                //    : header == 'Allarmi totali' ? 'Total alarms' : header == 'Total alarms' ? 'Allarmi totali' 
                //    : header == 'Tags (stato batteria)' ? 'Tags (battery status)' : header == 'Tags (battery status)' ? 'Tags (stato batteria)' 
                //    : header == 'Tag regolari' ? 'Regular tag'  : header == 'Regular tag' ? 'Tag regolari' 
                //    : ''
                //),
                defaultPosition,
                visible,
            }
        ));
        localStorage.setItem(`tileState: ${userEmail}`, JSON.stringify(tileStateToSave));
    };

    const onToggleWidget = (id: any) => {
        const updatedTiles = tiles.map((tile: any) =>
            tile.id === id ? { ...tile, visible: !tile.visible } : tile
        );
        setTiles(updatedTiles); // Set and save state immediately after toggling visibility
        saveTilesToLocalStorage(updatedTiles); // Immediately save visibility toggle
    };

    // refreshAPI
    const defaultValueRefreshAPI = [1, 10, 20, 30, 40, 50, 60, 70];
    const [valueRefreshAPI, setValueRefreshAPI] = useState(30);

    const onChangeValueRefreshAPI = (event: SliderChangeEvent) => {
        setValueRefreshAPI(Math.round(event.value));
        localStorage.setItem('refreshAPI', stringify(Math.round(event.value)))
    };

    useEffect(() => {
        let refreshAPI: any = localStorage.getItem('refreshAPI')
        if (refreshAPI != null) {
            setValueRefreshAPI(refreshAPI)
        } else {
            localStorage.setItem('refreshAPI', stringify(Math.round(valueRefreshAPI)))
        }
    }, [])


    // regularTag
    const onChangeValueRegularTag = (event: SliderChangeEvent) => {
        props.regularTag(Math.round(event.value));
        const newValueRegularTag: any = Math.round(event.value)
        setValueRegularTag(newValueRegularTag);
        localStorage.setItem('regularTag', stringify(newValueRegularTag))
    };

    useEffect(() => {
        let regularTag: any = localStorage.getItem('regularTag')
        if (regularTag != null) {
            setValueRegularTag(regularTag)
        } else {
            localStorage.setItem('regularTag', stringify(Math.round(valueRegularTag)))
        }
    }, [])


    // batteryPercentage
    const defaultValueUnloadTag = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    const [valueUnloadTag, setValueUnloadTag] = useState(30);

    const onChangeValueUnloadTag = (event: SliderChangeEvent) => {
        props.batteryPercentage(Math.round(event.value));
        setValueUnloadTag(Math.round(event.value));
        localStorage.setItem('batteryPercentage', stringify(Math.round(event.value)))
    };

    useEffect(() => {
        let batteryPercentage: any = localStorage.getItem('batteryPercentage')
        if (batteryPercentage != null) {
            setValueUnloadTag(Math.round(batteryPercentage))
        } else {
            localStorage.setItem('batteryPercentage', stringify(Math.round(valueUnloadTag)))
        }
    }, [])

    // statusDevice
    const defaultValueStatusDevice = [1, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
    const [valueStatusDevice, setValueStatusDevice] = useState(75);

    const onChangeValueStatusDevice = (event: SliderChangeEvent) => {
        props.statusDevice(Math.round(event.value));
        setValueStatusDevice(Math.round(event.value));
        localStorage.setItem('statusDevice', stringify(Math.round(event.value)))
    };

    useEffect(() => {
        let statusDevice: any = localStorage.getItem('statusDevice')
        if (statusDevice != null) {
            setValueStatusDevice(Math.round(statusDevice))
        } else {
            localStorage.setItem('statusDevice', stringify(Math.round(valueStatusDevice)))
        }
    }, [])

    const getClientConfig = async (accountId: any) => {
        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/settings/Client?clientId=${accountId}`, { headers })
                .then((res) => {
                    const data = res?.data?.data
                    setClientConfig(data);
                })
                .catch((error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    useEffect(() => {
        getClientConfig(accountId)
    },[])

    const updateClientConfig = async (languageParams: any) => {

        const languageValue = languageParams.value.localeId
        // aggiorno il valore di Language
        const updatedSettings = clientConfig.map((item:any) =>
                item.id === "Language" ? { ...item, value: languageValue == 'it-IT' ? '1' : '0' } : item
            )
        //const params = {
        //    settings: updatedSettings
        //};

        const params = {
            settings: [{
                id: 'Language',
                group: 'VIE',
                description: 'Smart Locator Language',
                value: languageValue == 'it-IT' ? '1' : '0',
                clientId: Number(accountId)
            }]
        };

        console.log(params);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            
            try {
                await axios.put(`${SERVER_URL}/settings/Client`, params, { headers })
            } catch (error: any) {
                console.error("Errore:", error);
            }
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken)
            })
        }
    }

    return (
        <div className="k-d-flex-col" style={{ height: '100%', padding: '20px', backgroundColor: '#F7F7F7' }}>

            {/* set positions of the widgets */}
            {/* <div className="header k-d-flex">
                <div className="title">IMPOSTAZIONI VARIE</div>
                <span className="k-spacer" />
                <div className="k-d-flex k-align-items-center">
                    <ButtonGroup>
                        <Button
                            togglable={true}
                            selected={isHorizontal}
                            onClick={horizontalOnClick}
                        >
                            Horizontal
                        </Button>
                        <Button
                            togglable={true}
                            selected={!isHorizontal}
                            onClick={verticalOnClick}
                        >
                            Vertical
                        </Button>
                    </ButtonGroup>
                </div>
            </div> */}

            <div className="settings">

                <div className="language-chooser">
                    <span style={{ padding: '5px 10px 0 0', color: 'black' }}>{localizationService.toLanguageString('language', 'NOT FOUND')}</span>
                    <DropDownList
                        className="button-language"
                        textField={'locale'}
                        dataItemKey={'localeId'}
                        data={locales}
                        value={currentLanguage}
                        onChange={(e: any) => {
                            onLanguageChange(e);
                            onLanguageChangeSystem(e);
                            updateClientConfig(e);
                            //updateTilesToLocalStorage(tiles);
                        }}
                    />
                </div>

                <span style={{ padding: '10px', color: 'black' }}>{localizationService.toLanguageString('version', 'NOT FOUND')}: {packageJson.version}</span>
                {/*<ThemeChooser changeTheme={changeTheme} theme={theme} />*/}
            </div>


            <StackLayout
                orientation={isHorizontal ? "horizontal" : "vertical"}
                gap={20}
                style={{ height: '300px', borderTop: '1px solid #e5e5e5', paddingTop: 30 }}
            >
                {/* Toggle widgets */}
                {/*<Card className="k-flex-20" >
                    <CardHeader className="k-hbox">
                        <div>
                            <CardTitle>{localizationService.toLanguageString('settings.toggle_widget', 'NOT FOUND')}</CardTitle>
                            <CardSubtitle 
                                style={{ fontSize: 'clamp(9px, 0.8vw, 16px)'}}
                            >{localizationService.toLanguageString('settings.toggle_widget_description', 'NOT FOUND')}</CardSubtitle>
                        </div>
                    </CardHeader>
                    <CardBody>
                        <div style={{ fontSize: '15px'}}>
                            {
                                tiles?.map((tile: any) => {
                                    if(tile.header.includes('Tag regolari') || tile.header.includes('Regular tag')) {
                                        tile.header = removeCharsUntilClosingParenthesis(tile.header)  
                                    }
                                    const titleConverted:any = tile.id == '0' ? 'devices_title' 
                                    : tile.id == '1' ? 'devices_counter' 
                                    : tile.id == '2' ? 'alarms_counter' 
                                    : tile.id == '3' ? 'battery_status' 
                                    : tile.id == '4' ? 'regular_tag' 
                                    : ''
                                    return (
                                        <div className="k-mb-2"  style={{ fontSize: 'clamp(9px, 0.8vw, 16px)', whiteSpace: 'nowrap'}} key={tile.id}>
                                            <Switch
                                                checked={tile.visible}
                                                onChange={()=>onToggleWidget(tile.id)}
                                                id={tile.id}
                                            />
                                            <label className="k-ml-3">
                                                {localizationService.toLanguageString(`settings.${titleConverted}`, 'NOT FOUND')}
                                            </label>
                                        </div>
                                    );
                                })}
                        </div>
                    </CardBody>
                    <CardActions>
                        <Button fillMode="flat" themeColor={"primary"} onClick={onResetLayoutWidget}>
                            {localizationService.toLanguageString('reset', 'NOT FOUND')}
                        </Button>
                    </CardActions>
                </Card>*/}

                {/* Refresh API */}
                <Card className="k-flex-20" style={{ minWidth: '320px' }}>
                    <CardHeader className="k-hbox">
                        <div>
                            <CardTitle>{localizationService.toLanguageString('settings.refresh_api', 'NOT FOUND')} (s)</CardTitle>
                            {/*<CardSubtitle>{localizationService.toLanguageString('settings.refresh_api_description', 'NOT FOUND')}</CardSubtitle>*/}
                        </div>
                    </CardHeader>
                    <CardBody>
                        <span
                            style={{
                                display: 'block',
                                textAlign: 'center',
                                position: 'relative',
                                top: '10%',
                                fontWeight: 'bold',
                                fontSize: '30px',
                                color: '#018CA5'
                            }}
                        >
                            {/*{localStorage.getItem('refreshAPI')}s*/}
                            {valueRefreshAPI}s
                        </span>
                        <Slider
                            // vertical={true}
                            min={1}
                            max={70}
                            step={1}
                            value={valueRefreshAPI}
                            onChange={onChangeValueRefreshAPI}
                        >
                            {defaultValueRefreshAPI.map((value: any, id: any) => (
                                <SliderLabel
                                    title={value.toString() + ' ' + localizationService.toLanguageString('settings.seconds', 'NOT FOUND')}
                                    key={id}
                                    position={value}
                                    onClick={() => {
                                        setValueRefreshAPI(value);
                                    }}
                                >
                                    {value.toString()}
                                    {/*{value.toString() + ' s'}*/}
                                </SliderLabel>
                            ))
                            }
                        </Slider>
                    </CardBody>
                    {/* <CardActions>
                        <Button fillMode="flat" themeColor={"primary"}>
                            Salva
                        </Button>
                    </CardActions> */}
                </Card>

                {/* Regular Tag */}
                <Card className="k-flex-20" >
                    <CardHeader className="k-hbox">
                        <div>
                            <CardTitle>{localizationService.toLanguageString('settings.regular_tag_2', 'NOT_FOUND')} (h)</CardTitle>
                            {/*<CardSubtitle>{localizationService.toLanguageString('settings.regular_tag_description', 'NOT FOUND')}</CardSubtitle>*/}
                        </div>
                    </CardHeader>
                    <CardBody>
                        <span
                            style={{
                                display: 'block',
                                textAlign: 'center',
                                position: 'relative',
                                top: '10%',
                                fontWeight: 'bold',
                                fontSize: '30px',
                                color: '#018CA5'
                            }}
                        >
                            {valueRegularTag}h
                        </span>
                        <Slider
                            // vertical={true}
                            min={0}
                            max={48}
                            step={1}
                            value={valueRegularTag}
                            onChange={onChangeValueRegularTag}
                        >
                            {defaultValueRegularTag.map((value: any, id: any) => (
                                <SliderLabel
                                    title={value.toString() + ' hours'}
                                    key={id}
                                    position={value}
                                    onClick={() => {
                                        setValueRegularTag(value);
                                    }}
                                >
                                    {value.toString()}
                                    {/*{value.toString() + ' h'}*/}
                                </SliderLabel>
                            ))
                            }
                        </Slider>
                    </CardBody>
                    {/* <CardActions>
                        <Button fillMode="flat" themeColor={"primary"}>
                            Salva
                        </Button>
                    </CardActions> */}
                </Card>

                {/* Tag scariche */}
                <Card className="k-flex-20" >
                    <CardHeader className="k-hbox">
                        <div>
                            <CardTitle>{localizationService.toLanguageString('settings.unload_tag_2', 'NOT FOUND')} (%)</CardTitle>
                            {/*<CardSubtitle>{localizationService.toLanguageString('settings.unload_tag_description', 'NOT FOUND')}</CardSubtitle>*/}
                        </div>
                    </CardHeader>
                    <CardBody>
                        <span
                            style={{
                                display: 'block',
                                textAlign: 'center',
                                position: 'relative',
                                top: '10%',
                                fontWeight: 'bold',
                                fontSize: '30px',
                                color: '#018CA5'
                            }}
                        >
                            {valueUnloadTag}%
                        </span>
                        <Slider
                            // vertical={true}
                            min={10}
                            max={100}
                            step={10}
                            value={valueUnloadTag}
                            onChange={onChangeValueUnloadTag}
                        >
                            {defaultValueUnloadTag.map((value: any, id: any) => (
                                <SliderLabel
                                    title={value.toString() + ' %'}
                                    key={id}
                                    position={value}
                                    onClick={() => {
                                        setValueUnloadTag(value);
                                    }}
                                >
                                    {/*{value.toString() + '%'}*/}
                                    {value.toString()}
                                </SliderLabel>
                            ))
                            }
                        </Slider>
                    </CardBody>
                    {/* <CardActions>
                        <Button fillMode="flat" themeColor={"primary"}>
                            Salva
                        </Button>
                    </CardActions> */}
                </Card>

                {/* Status device */}
                <Card className="k-flex-20" >
                    <CardHeader className="k-hbox">
                        <div>
                            <CardTitle>{localizationService.toLanguageString('settings.status_device', 'NOT FOUND')} (min)</CardTitle>
                            {/*<CardSubtitle>{localizationService.toLanguageString('settings.status_device_description', 'NOT FOUND')}</CardSubtitle>*/}
                        </div>
                    </CardHeader>
                    <CardBody>
                        <span
                            style={{
                                display: 'block',
                                textAlign: 'center',
                                position: 'relative',
                                top: '10%',
                                fontWeight: 'bold',
                                fontSize: '30px',
                                color: '#018CA5'
                            }}
                        >
                            {valueStatusDevice}min
                        </span>
                        <Slider
                            min={1}
                            max={100}
                            step={10}
                            value={valueStatusDevice}
                            onChange={onChangeValueStatusDevice}
                        >
                            {defaultValueStatusDevice.map((value: any, id: any) => (
                                <SliderLabel
                                    title={value.toString() + ' min'}
                                    key={id}
                                    position={value}
                                    onClick={() => {
                                        setValueStatusDevice(value);
                                    }}
                                >
                                    {value.toString()}
                                </SliderLabel>
                            ))
                            }
                        </Slider>
                    </CardBody>
                </Card>

            </StackLayout>

        </div>
    )
}